import React, { useState } from 'react';
import { MapPin, Maximize2 } from 'lucide-react';
import { FinanceiroTabela } from './index';
import Modal from 'components/common/Modal';

const AnaliseCidadeSection = ({ dadosAnaliseCidade }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Verificar se os dados estão disponíveis
  if (!dadosAnaliseCidade || dadosAnaliseCidade.length === 0) {
    return (
      <div className="overflow-hidden h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 min-w-0">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center min-w-0 flex-1">
            <MapPin className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0" />
            <span className="break-words leading-tight">Análise por Cidade</span>
          </h3>
        </div>
        <p className="text-sm text-[color:var(--text-muted)] mb-4">Análise detalhada de recebimentos e ticket médio por cidade</p>
        <div className="text-center text-[color:var(--text-muted)] py-8 flex-1 flex items-center justify-center">
          Nenhum dado disponível para análise
        </div>
      </div>
    );
  }

  const colunas = [
    { chave: 'cidade', titulo: 'Cidade', alinhamento: 'left' },
    { chave: 'contratos_distintos', titulo: 'Contratos', alinhamento: 'right' },
    { chave: 'valor_total', titulo: 'Valor Previsto (R$)', alinhamento: 'right', formato: 'moeda' },
    { chave: 'valor_recebido', titulo: 'Valor Recebido (R$)', alinhamento: 'right', formato: 'moeda' },
    { chave: 'taxa_recebimento', titulo: 'Taxa de Recebimento (%)', alinhamento: 'right', formato: 'percentual' },
    { chave: 'participacao', titulo: 'Participação (%)', alinhamento: 'right', formato: 'percentual' },
    { chave: 'ticket_medio', titulo: 'Ticket Médio (R$)', alinhamento: 'right', formato: 'moeda' }
  ];

  // Colunas para o modal com títulos adicionais
  const colunasModal = [
    { chave: 'cidade', titulo: 'Cidade', alinhamento: 'left', largura: '20%' },
    { chave: 'contratos_distintos', titulo: 'Contratos', alinhamento: 'right', largura: '10%' },
    { chave: 'valor_total', titulo: 'Valor Previsto (R$)', alinhamento: 'right', formato: 'moeda', largura: '14%' },
    { chave: 'valor_recebido', titulo: 'Valor Recebido (R$)', alinhamento: 'right', formato: 'moeda', largura: '14%' },
    { chave: 'titulos_total', titulo: 'Títulos Emitidos', alinhamento: 'right', largura: '12%' },
    { chave: 'titulos_recebidos', titulo: 'Títulos Recebidos', alinhamento: 'right', largura: '12%' },
    { chave: 'taxa_recebimento', titulo: 'Taxa de Recebimento (%)', alinhamento: 'right', formato: 'percentual', largura: '10%' },
    { chave: 'participacao', titulo: 'Participação', alinhamento: 'right', formato: 'percentual', largura: '10%' },
    { chave: 'ticket_medio', titulo: 'Ticket Médio (R$)', alinhamento: 'right', formato: 'moeda', largura: '10%' }
  ];

  const dadosProcessados = dadosAnaliseCidade.map(item => ({
    cidade: item.cidade || 'N/A',
    contratos_distintos: item.contratos_distintos || 0,
    valor_total: item.valor_total || 0,
    valor_recebido: item.valor_recebido || 0,
    titulos_total: item.titulos_total || 0,
    titulos_recebidos: item.titulos_recebidos || 0,
    taxa_recebimento: item.taxa_recebimento || 0,
    participacao: item.participacao || 0,
    ticket_medio: item.ticket_medio || 0
  }));

  const totaisConsolidados = dadosProcessados.reduce((totais, item) => {
    totais.contratos_distintos += Number(item.contratos_distintos) || 0;
    totais.valor_total += Number(item.valor_total) || 0;
    totais.valor_recebido += Number(item.valor_recebido) || 0;
    totais.titulos_total += Number(item.titulos_total) || 0;
    totais.titulos_recebidos += Number(item.titulos_recebidos) || 0;
    return totais;
  }, {
    contratos_distintos: 0,
    valor_total: 0,
    valor_recebido: 0,
    titulos_total: 0,
    titulos_recebidos: 0
  });

  const totalConsolidado = totaisConsolidados.titulos_recebidos > 0
    ? totaisConsolidados.valor_recebido / totaisConsolidados.titulos_recebidos
    : (totaisConsolidados.titulos_total > 0
      ? totaisConsolidados.valor_total / totaisConsolidados.titulos_total
      : 0);

  return (
    <>
      <div className="overflow-hidden h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 min-w-0">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center min-w-0 flex-1">
            <MapPin className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0" />
            <span className="break-words leading-tight">Análise por Cidade</span>
          </h3>
          <button
            onClick={() => setIsModalOpen(true)}
            className="ml-2 p-2 text-[color:var(--text-muted)] hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex-shrink-0"
            title="Ver todas as cidades"
            aria-label="Ampliar tabela"
          >
            <Maximize2 className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-[color:var(--text-muted)] mb-4">Análise detalhada de recebimentos e ticket médio por cidade</p>
        
        {/* Desktop: Tabela tradicional */}
        <div className="hidden md:block flex-1 min-h-0">
          <FinanceiroTabela
            colunas={colunas}
            dados={dadosProcessados}
            compacto={true}
            altura="h-full"
          />
        </div>

        {/* Mobile: Layout de cards */}
        <div className="md:hidden flex-1 min-h-0">
          <div className="space-y-4 h-full overflow-y-auto">
          {dadosProcessados.map((item, index) => (
            <div key={index} className="bg-[color:var(--surface-muted)] rounded-lg p-4 border border-[color:var(--border)]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[color:var(--text-primary)] text-sm">{item.cidade}</h3>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]">
                  Ticket médio
                </span>
              </div>
              
                <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="text-[color:var(--text-muted)] mb-1">Contratos</p>
                  <p className="font-medium text-[color:var(--text-primary)]">
                    {item.contratos_distintos.toLocaleString('pt-BR')}
                  </p>
                </div>

                <div>
                  <p className="text-[color:var(--text-muted)] mb-1">Valor Total</p>
                  <p className="font-medium text-[color:var(--text-primary)]">
                    R$ {item.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                
                <div>
                  <p className="text-[color:var(--text-muted)] mb-1">Taxa Recebimento</p>
                  <p className="font-medium text-[color:var(--text-primary)]">
                    {item.taxa_recebimento.toFixed(1)}%
                  </p>
                </div>

                <div>
                  <p className="text-[color:var(--text-muted)] mb-1">Valor Recebido</p>
                  <p className="font-medium text-[color:var(--text-primary)]">
                    R$ {item.valor_recebido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                
                <div className="col-span-2">
                  <p className="text-[color:var(--text-muted)] mb-1">Ticket Médio</p>
                  <p className="font-medium text-[color:var(--text-primary)]">
                    R$ {item.ticket_medio.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="col-span-2">
                  <p className="text-[color:var(--text-muted)] mb-1">Participação</p>
                  <p className="font-medium text-[color:var(--text-primary)]">
                    {item.participacao.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>

      {/* Modal com tabela completa */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Análise Completa por Cidade"
        size="xl"
      >
        <div className="flex max-h-[72vh] flex-col gap-4 overflow-hidden">
          <div className="h-[52vh] overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
            <FinanceiroTabela
              colunas={colunasModal}
              dados={dadosProcessados}
              compacto={false}
              altura="h-full"
              className="border-0 rounded-none"
            />
          </div>

          <div className="flex-shrink-0 rounded-2xl border border-[color:var(--border)] bg-[linear-gradient(180deg,var(--surface-muted),var(--surface))] px-4 py-5 shadow-sm">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
              <div className="min-h-[76px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
                <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Total de cidades</p>
                <p className="mt-2 text-base font-semibold text-[color:var(--text-primary)]">
                  {dadosProcessados.length}
                </p>
              </div>
              <div className="min-h-[76px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
                <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Contratos</p>
                <p className="mt-2 text-base font-semibold text-[color:var(--text-primary)]">
                  {totaisConsolidados.contratos_distintos.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="min-h-[76px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
                <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Valor previsto</p>
                <p className="mt-2 text-base font-semibold text-[color:var(--text-primary)]">
                  R$ {totaisConsolidados.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="min-h-[76px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
                <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Valor recebido</p>
                <p className="mt-2 text-base font-semibold text-[color:var(--text-primary)]">
                  R$ {totaisConsolidados.valor_recebido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="min-h-[76px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
                <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Títulos emitidos</p>
                <p className="mt-2 text-base font-semibold text-[color:var(--text-primary)]">
                  {totaisConsolidados.titulos_total.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="min-h-[76px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
                <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Títulos recebidos</p>
                <p className="mt-2 text-base font-semibold text-[color:var(--text-primary)]">
                  {totaisConsolidados.titulos_recebidos.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="min-h-[76px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
                <p className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Ticket médio</p>
                <p className="mt-2 text-base font-semibold text-[color:var(--text-primary)]">
                  R$ {totalConsolidado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AnaliseCidadeSection;
